import React, { createContext, useContext, useEffect, useState } from 'react'
import { StorageEngine } from '@innolab-trader/storage'
import { PreferencesManager } from '../preferences'

export interface ThemeContextType {
  theme: 'dark' | 'light'
  isDark: boolean
  toggleTheme: () => void
  setTheme: (theme: 'dark' | 'light') => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const THEME_KEY = 'innolab-trader-theme'

interface ThemeProviderProps {
  children: React.ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Start with a placeholder; we won't write or change DOM until loaded
  const [theme, setThemeState] = useState<'dark' | 'light'>('light')
  const [ready, setReady] = useState(false)

  // Initial load from storage (prefer UserPreferences, fallback to ThemeProvider key)
  useEffect(() => {
    (async () => {
      let initial: 'dark' | 'light' = 'light'
      try {
        const engine = StorageEngine.local()
        // 1) Try user preferences (supports 'system')
        const user = await PreferencesManager.loadUserPrefs(engine)
        if (user?.theme === 'dark' || user?.theme === 'light') {
          initial = user.theme
        } else if (user?.theme === 'system') {
          const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
          initial = prefersDark ? 'dark' : 'light'
        } else {
          // 2) Fallback to non-namespaced theme key used by shell bootstrap
          const doc = engine.document<string>(THEME_KEY)
          const stored = await doc.load()
          if (stored === 'light' || stored === 'dark') initial = stored
          else initial = 'light'
        }
      } catch {
        initial = 'light'
      }
      setThemeState(initial)
      setReady(true)
    })()
  }, [])

  const isDark = theme === 'dark'

  useEffect(() => {
    if (!ready) return
    const root = document.documentElement
    root.classList.remove('light', 'dark')
    root.classList.add(theme)

    // Persist
    try {
      const engine = StorageEngine.local()
      // Write-through to both non-namespaced key and user preferences (for cross-device sync)
      engine.document<string>(THEME_KEY).save(theme)
      PreferencesManager.setTheme(engine, theme).catch(() => {})
    } catch {}

    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      // Match new design system backgrounds
      metaThemeColor.setAttribute('content', theme === 'dark' ? '#0D0E12' : '#FAFBFC')
    }
  }, [theme, ready])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = (e: MediaQueryListEvent) => {
      (async () => {
        try {
          const hasPref = await StorageEngine.local().document<string>(THEME_KEY).load()
          if (!hasPref) setThemeState(e.matches ? 'dark' : 'light')
        } catch {
          setThemeState(e.matches ? 'dark' : 'light')
        }
      })()
    }
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark'
    setThemeState(newTheme)

    // 🎉 Fun console messages for theme switching
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (((import.meta as any)?.env?.DEV)) {
      if (newTheme === 'dark') {
        console.log('🥷 Ninja Mode Activated - Stealth Trading Engaged')
        console.log('%cWelcome to the shadows...', 'color: #FF6600; font-weight: bold; font-size: 14px;')
      } else {
        console.log('☀️ Sunshine Mode Activated - Time to Make It Rain!')
        console.log('%cLet there be light!', 'color: #257FFF; font-weight: bold; font-size: 14px;')
      }
    }
  }

  const setTheme = (newTheme: 'dark' | 'light') => {
    setThemeState(newTheme)

    // Fun console messages
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (((import.meta as any)?.env?.DEV)) {
      if (newTheme === 'dark') {
        console.log('🥷 Ninja Mode - Silent but deadly trading')
      } else {
        console.log('☀️ Sunshine Mode - Bright ideas, brighter profits')
      }
      try {
        const engine = StorageEngine.local()
        PreferencesManager.setTheme(engine, newTheme).catch(() => {})
      } catch {}
    }
  }

  const value: ThemeContextType = { theme, isDark, toggleTheme, setTheme }
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext)
  if (context === undefined) throw new Error('useTheme must be used within a ThemeProvider')
  return context
}

export function useThemeClass(darkClass: string, lightClass: string): string {
  const { isDark } = useTheme()
  return isDark ? darkClass : lightClass
}

export function useThemeValue<T>(darkValue: T, lightValue: T): T {
  const { isDark } = useTheme()
  return isDark ? darkValue : lightValue
}
